import { Fragment, useMemo, useState } from "react";
import { Tooltip } from "@/components/ui/Tooltip";
import { JsonViewerModal } from "./JsonViewerModal";
import type { UnifiedSubRow, RowStatus, SubTier } from "./productResolver";

interface Props {
    rows: UnifiedSubRow[];
}

const formatDate = (ts: number | null): string => {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

/**
 * The raw store receipt is an escaped JSON-in-JSON envelope. Parse it into a pretty-printed,
 * 2-space-indented string for the modal. Also expand the nested `jsonRepresentation` (iOS) so the
 * real transaction fields are readable rather than a single escaped blob. Falls back to the raw
 * string if it isn't valid JSON.
 */
// Real receipts are ~1.5-2 KB. Skip the parse+stringify on anything far larger so a malformed or
// inflated blob can't freeze the admin UI; show it raw (truncated) instead. (codex review.)
const MAX_PRETTY_PRINT_BYTES = 256 * 1024;

const prettyPrintReceipt = (raw: string): string => {
    if (raw.length > MAX_PRETTY_PRINT_BYTES) {
        return `[blob too large to pretty-print — ${raw.length} chars; showing first 256KB raw]\n\n${raw.slice(0, MAX_PRETTY_PRINT_BYTES)}`;
    }
    try {
        const outer = JSON.parse(raw);
        if (outer && typeof outer.jsonRepresentation === "string") {
            try {
                outer.jsonRepresentation = JSON.parse(outer.jsonRepresentation);
            } catch {
                /* leave as string if the nested blob isn't JSON */
            }
        }
        return JSON.stringify(outer, null, 2);
    } catch {
        return raw;
    }
};

const STATUS_META: Record<RowStatus, { label: string; cls: string }> = {
    active: { label: "Active", cls: "bg-green-100 text-green-800" },
    superseded: { label: "Superseded", cls: "bg-gray-100 text-gray-600" },
    expired: { label: "Expired", cls: "bg-gray-100 text-gray-600" },
    trial_expired: { label: "Trial ended", cls: "bg-gray-100 text-gray-600" },
    deleted: { label: "Deleted grant", cls: "bg-red-100 text-red-700" },
    unknown: { label: "Unknown", cls: "bg-amber-100 text-amber-800" },
};

const TIER_CLS: Record<SubTier, string> = {
    pro: "bg-blue-100 text-blue-800",
    plus: "bg-purple-100 text-purple-800",
    trial: "bg-purple-50 text-purple-700",
    free: "bg-gray-100 text-gray-700",
    unknown: "bg-amber-100 text-amber-800",
};

type SourceFilter = "all" | "store" | "stripe" | "free_grant";
/** Tier filter uses the PUBLIC product names ('light' is the legacy stored name for Plus). */
type TierFilter = "all" | "pro" | "plus" | "free";

const isHistorical = (s: RowStatus) => s === "superseded" || s === "expired" || s === "trial_expired" || s === "deleted";

// Sortable columns and how to read a comparable value off a row.
type SortKey = "status" | "tier" | "source" | "platform" | "country" | "price" | "purchase" | "expiry" | "cancel" | "consent" | "reference";
type SortDir = "asc" | "desc";

const sortValue = (r: UnifiedSubRow, key: SortKey): string | number => {
    switch (key) {
        case "status": return r.status;
        case "tier": return r.tier;
        case "source": return r.source;
        case "platform": return r.platformLabel;
        case "country": return r.country?.alpha2 ?? "";
        case "price": {
            // sort by numeric magnitude when parseable, else push to the end
            const n = parseFloat((r.price ?? "").replace(/[^0-9.]/g, ""));
            return Number.isFinite(n) ? n : -1;
        }
        case "purchase": return r.purchaseDate ?? 0;
        case "expiry": return r.expiryDate ?? 0;
        case "cancel": return r.cancelReason?.label ?? "";
        case "consent": return r.priceConsent?.label ?? "";
        case "reference": return r.reference ?? "";
    }
};

// Free-text haystack for the search box.
const rowHaystack = (r: UnifiedSubRow): string =>
    [r.productLabel, r.rawProductId, r.platformLabel, r.source, r.status, r.country?.name, r.country?.alpha2,
     r.price, r.cancelReason?.label, r.priceConsent?.label, r.reference, r.observations]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

export const UnifiedSubscriptionsTable: React.FC<Props> = ({ rows }) => {
    const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
    const [tierFilter, setTierFilter] = useState<TierFilter>("all");
    // Default ON: support usually wants the full picture, and a user whose only subs are expired
    // would otherwise see an empty table. (Operator request.)
    const [includeHistorical, setIncludeHistorical] = useState(true);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [search, setSearch] = useState("");
    // null sortKey = default ordering (active first, then date desc).
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    // Raw store JSON shown in a modal (pretty-printed) rather than cramped inline.
    const [rawJsonModal, setRawJsonModal] = useState<string | null>(null);


    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            // dates default to desc (newest first); text defaults to asc.
            setSortDir(key === "purchase" || key === "expiry" ? "desc" : "asc");
        }
    };

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = rows
            .filter((r) => (sourceFilter === "all" ? true : r.source === sourceFilter))
            // "free" means the FREE tier, which no paid row can carry — a free grant is a
            // comped Pro/Plus, so it must not be swept up by a tier=free filter.
            .filter((r) => (tierFilter === "all" ? true : r.tier === tierFilter))
            .filter((r) => (includeHistorical ? true : !isHistorical(r.status)))
            .filter((r) => (q ? rowHaystack(r).includes(q) : true));

        if (sortKey == null) {
            // Default: active first, then by purchase date desc.
            return [...filtered].sort((a, b) => {
                const ah = isHistorical(a.status) ? 1 : 0;
                const bh = isHistorical(b.status) ? 1 : 0;
                if (ah !== bh) return ah - bh;
                return (b.purchaseDate ?? 0) - (a.purchaseDate ?? 0);
            });
        }
        const dir = sortDir === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const av = sortValue(a, sortKey);
            const bv = sortValue(b, sortKey);
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
    }, [rows, sourceFilter, tierFilter, includeHistorical, search, sortKey, sortDir]);

    const historicalCount = rows.filter((r) => isHistorical(r.status)).length;

    return (
        <div>
            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
                        {(["all", "store", "stripe", "free_grant"] as SourceFilter[]).map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setSourceFilter(s)}
                                className={`px-3 py-1.5 font-medium transition-colors ${
                                    sourceFilter === s ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {s === "all" ? "All" : s === "store" ? "Store" : s === "stripe" ? "Stripe" : "Free grants"}
                            </button>
                        ))}
                    </div>
                    {/* Tier filter — asked for by ops: "filter on subscription type". */}
                    <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
                        {(["all", "pro", "plus", "free"] as TierFilter[]).map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTierFilter(t)}
                                className={`px-3 py-1.5 font-medium transition-colors ${
                                    tierFilter === t ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {t === "all" ? "All tiers" : t === "pro" ? "PRO" : t === "plus" ? "Plus" : "Free"}
                            </button>
                        ))}
                    </div>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search product, order #, country…"
                        className="px-2.5 py-1.5 rounded-md border border-gray-200 text-xs w-56 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                </div>
                {historicalCount > 0 && (
                    <button
                        type="button"
                        role="switch"
                        aria-checked={includeHistorical}
                        onClick={() => setIncludeHistorical((v) => !v)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            includeHistorical
                                ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                        <span
                            className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                includeHistorical ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-white"
                            }`}
                        >
                            {includeHistorical && (
                                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.1 3.1 6.8-6.8a1 1 0 011.4 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </span>
                        Include expired &amp; deleted
                        <span className={includeHistorical ? "text-blue-500" : "text-gray-400"}>({historicalCount})</span>
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr className="text-left text-xs font-medium text-gray-500">
                            {(
                                [
                                    ["status", "Status"],
                                    ["tier", "Tier / Product"],
                                    ["source", "Source"],
                                    ["platform", "Platform"],
                                    ["country", "Country"],
                                    ["price", "Price"],
                                    ["purchase", "Purchase"],
                                    ["expiry", "Expiry"],
                                    ["cancel", "Cancel reason"],
                                    ["consent", "Price consent"],
                                    ["reference", "Reference"],
                                ] as [SortKey, string][]
                            ).map(([key, label]) => (
                                <th key={key} className="px-3 py-2">
                                    <button
                                        type="button"
                                        onClick={() => toggleSort(key)}
                                        className="inline-flex items-center gap-1 font-medium hover:text-gray-700"
                                        title={`Sort by ${label}`}
                                    >
                                        {label}
                                        <span className="text-gray-400">
                                            {sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                                        </span>
                                    </button>
                                </th>
                            ))}
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {visible.map((r) => {
                            const sm = STATUS_META[r.status];
                            const muted = isHistorical(r.status);
                            const open = !!expanded[r.key];
                            return (
                                <Fragment key={r.key}>
                                    <tr className="bg-white">
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sm.cls}`}>
                                                {sm.label}
                                            </span>
                                        </td>
                                        <td className={`px-3 py-2 whitespace-nowrap ${muted ? "opacity-60" : ""}`}>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_CLS[r.tier]}`}>
                                                {r.productLabel}
                                            </span>
                                            {!r.verified && (
                                                <Tooltip content="Tier/expiry could not be verified from a decoded store receipt. Click ‘Revalidate with store’ to confirm.">
                                                    <span className="ml-1 cursor-help text-amber-500" aria-label="unverified">
                                                        ⚠
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </td>
                                        <td className={`px-3 py-2 whitespace-nowrap text-gray-700 ${muted ? "opacity-60" : ""}`}>
                                            {r.source === "store" ? "Store" : r.source === "stripe" ? "Stripe" : "Free grant"}
                                        </td>
                                        <td className={`px-3 py-2 whitespace-nowrap text-gray-700 ${muted ? "opacity-60" : ""}`}>
                                            {r.platformLabel}
                                        </td>
                                        <td className={`px-3 py-2 whitespace-nowrap text-gray-700 ${muted ? "opacity-60" : ""}`}>
                                            {r.country ? (
                                                <Tooltip content={`Purchase storefront: ${r.country.name}`}>
                                                    <span className="cursor-help">
                                                        {r.country.flag} {r.country.alpha2}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className={`px-3 py-2 whitespace-nowrap text-gray-700 ${muted ? "opacity-60" : ""}`}>
                                            {r.price ?? <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className={`px-3 py-2 whitespace-nowrap text-gray-700 ${muted ? "opacity-60" : ""}`}>
                                            {formatDate(r.purchaseDate)}
                                        </td>
                                        <td className={`px-3 py-2 whitespace-nowrap text-gray-700 ${muted ? "opacity-60" : ""}`}>
                                            {r.expiryDate ? (
                                                formatDate(r.expiryDate)
                                            ) : (
                                                <Tooltip content="No verified expiry on this receipt. We never show a guessed/placeholder date.">
                                                    <span className="cursor-help text-amber-600">Expiry unknown</span>
                                                </Tooltip>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {r.cancelReason ? (
                                                <Tooltip content={r.cancelReason.explainer}>
                                                    <span
                                                        className={`inline-flex items-center gap-1 cursor-help text-xs font-medium ${
                                                            r.cancelReason.tone === "warn"
                                                                ? "text-amber-700"
                                                                : r.cancelReason.tone === "info"
                                                                ? "text-blue-700"
                                                                : "text-gray-700"
                                                        }`}
                                                    >
                                                        {r.cancelReason.label}
                                                        <span className="text-gray-400">ⓘ</span>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {r.priceConsent ? (
                                                <Tooltip content={r.priceConsent.explainer}>
                                                    <span
                                                        className={`inline-flex items-center gap-1 cursor-help text-xs font-medium ${
                                                            r.priceConsent.tone === "warn"
                                                                ? "text-amber-700"
                                                                : r.priceConsent.tone === "ok"
                                                                ? "text-green-700"
                                                                : "text-gray-600"
                                                        }`}
                                                    >
                                                        {r.priceConsent.label}
                                                        <span className="text-gray-400">ⓘ</span>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <span className="text-gray-400" title="Not yet polled by the store-reconcile cron">—</span>
                                            )}
                                        </td>
                                        <td className={`px-3 py-2 text-xs font-mono text-gray-600 max-w-[160px] truncate ${muted ? "opacity-60" : ""}`}>
                                            <span title={r.reference ?? ""}>{r.reference ?? "—"}</span>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-right">
                                            <button
                                                type="button"
                                                onClick={() => setExpanded((p) => ({ ...p, [r.key]: !p[r.key] }))}
                                                className="text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                {open ? "Hide" : "Details"}
                                            </button>
                                        </td>
                                    </tr>
                                    {open && (
                                        <tr className="bg-gray-50">
                                            <td colSpan={13} className="px-4 py-3">
                                                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600 mb-2">
                                                    <div>
                                                        <span className="font-medium text-gray-500">Raw product/SKU: </span>
                                                        <span className="font-mono">{r.rawProductId ?? "—"}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-500">Trust: </span>
                                                        {r.verified ? "Verified from store receipt" : "Unverified — not re-checked"}
                                                    </div>
                                                    {r.referenceLabel === "Granted by" && (
                                                        <div>
                                                            <span className="font-medium text-gray-500">Granted by: </span>
                                                            {r.reference ?? "—"}
                                                        </div>
                                                    )}
                                                    {r.fromMigration && (
                                                        <div>
                                                            <span className="font-medium text-gray-500">From migration: </span>yes
                                                        </div>
                                                    )}
                                                    {r.deletedAt && (
                                                        <div>
                                                            <span className="font-medium text-gray-500">Deleted at: </span>
                                                            {formatDate(r.deletedAt)}
                                                        </div>
                                                    )}
                                                    {r.observations && (
                                                        <div className="col-span-2">
                                                            <span className="font-medium text-gray-500">Observations: </span>
                                                            {r.observations}
                                                        </div>
                                                    )}
                                                </div>
                                                {r.source === "store" && (r.raw as { purchaseStringified?: string }).purchaseStringified && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setRawJsonModal(
                                                                prettyPrintReceipt(
                                                                    (r.raw as { purchaseStringified?: string }).purchaseStringified as string
                                                                )
                                                            )
                                                        }
                                                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                                                    >
                                                        View raw store JSON
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={13} className="px-4 py-6 text-center text-sm text-gray-500 italic">
                                    {rows.length === 0
                                        ? "This user has never had Pro/Plus, a trial, or a free grant."
                                        : !includeHistorical && historicalCount > 0
                                        ? `No active subscriptions — ${historicalCount} expired/deleted hidden. Tick “Include expired & deleted” to view.`
                                        : "No subscriptions match the current filters."}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Raw store JSON — pretty, syntax-highlighted, searchable modal. */}
            <JsonViewerModal value={rawJsonModal} onClose={() => setRawJsonModal(null)} />
        </div>
    );
};

export default UnifiedSubscriptionsTable;
